/**
 * FileUpload Component
 * Drag-and-drop file upload with Azure Blob Storage integration
 */

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { FolderOpen, CheckCircle2, XCircle, Clock, FileText, X as XIcon } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { storageService } from '../api';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const DropZone = styled.div`
  border: 2px dashed ${props => props.isDragActive
    ? props.theme.colors.tint
    : props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.xl};
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${props => props.isDragActive
    ? `${props.theme.colors.tint}10`
    : props.theme.colors.backgroundSecondary};

  &:hover {
    border-color: ${props => props.theme.colors.tint};
    background-color: ${props => props.theme.colors.tint}05;
  }
`;

const DropZoneIcon = styled.div`
  font-size: 48px;
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const DropZoneText = styled.p`
  color: ${props => props.theme.colors.text};
  margin: 0 0 ${props => props.theme.spacing.xs} 0;
  font-weight: 500;
`;

const DropZoneSubtext = styled.p`
  color: ${props => props.theme.colors.muted};
  margin: 0;
  font-size: ${props => props.theme.typography.sizes.small};
`;

const FileList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.sm};
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const FileIcon = styled.div`
  font-size: 24px;
  flex-shrink: 0;
`;

const FileInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const FileName = styled.div`
  font-weight: 500;
  color: ${props => props.theme.colors.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FileSize = styled.div`
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 4px;
  background-color: ${props => props.theme.colors.border};
  border-radius: 2px;
  overflow: hidden;
  margin-top: ${props => props.theme.spacing.xs};
`;

const ProgressFill = styled.div`
  height: 100%;
  background-color: ${props => props.theme.colors.tint};
  transition: width 0.3s ease;
  width: ${props => props.progress}%;
`;

const FileStatus = styled.div`
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.status === 'success'
    ? props.theme.colors.success || '#28a745'
    : props.status === 'error'
    ? props.theme.colors.danger || '#dc3545'
    : props.theme.colors.muted};
  margin-top: ${props => props.theme.spacing.xs};
`;

const RemoveButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.danger || '#dc3545'};
  cursor: pointer;
  padding: ${props => props.theme.spacing.xs};
  font-size: 20px;
  line-height: 1;
  flex-shrink: 0;

  &:hover {
    opacity: 0.7;
  }
`;

const HiddenInput = styled.input`
  display: none;
`;

/**
 * Format file size for display
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size
 */
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * FileUpload Component
 * @param {Object} props
 * @param {string[]} props.value - Array of uploaded blob paths
 * @param {Function} props.onChange - Callback with array of blob paths
 * @param {number} props.userId - User ID for generating temp paths
 * @param {number} [props.maxFiles] - Maximum number of files (default: 10)
 * @param {string} [props.accept] - Accepted file types (MIME types, e.g., "image/*")
 * @param {boolean} [props.disabled] - Disable upload
 */
export const FileUpload = ({
  value = [],
  onChange,
  userId,
  maxFiles = 10,
  accept,
  disabled = false
}) => {
  const theme = useTheme();
  const fileInputRef = useRef(null);

  const [isDragActive, setIsDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [storageConfig, setStorageConfig] = useState(null);
  const [sasToken, setSasToken] = useState(null);

  // Load storage config on mount
  useEffect(() => {
    const loadConfig = async () => {
      try {
        const config = await storageService.getStorageConfig();
        setStorageConfig(config);
      } catch (error) {
        console.error('Failed to load storage config:', error);
        toast.error('Failed to load upload configuration');
      }
    };
    loadConfig();
  }, []);

  // Sync value prop (blob paths from draft) with internal files state
  useEffect(() => {
    if (!value || value.length === 0) {
      // No blob paths - clear files
      setFiles([]);
      return;
    }

    // Check if we already have these files in state
    const currentBlobPaths = files
      .filter(f => f.status === 'success' && f.blobPath)
      .map(f => f.blobPath);

    const areSame = value.length === currentBlobPaths.length &&
      value.every(path => currentBlobPaths.includes(path));

    if (areSame) {
      // Already in sync
      return;
    }

    // Reconstruct files array from blob paths (for restored drafts)
    const reconstructedFiles = value.map((blobPath, index) => {
      // Extract filename from blob path (e.g., "temp/user123/image.jpg" -> "image.jpg")
      const filename = blobPath.split('/').pop();

      return {
        id: Date.now() + index,
        file: null, // No actual File object (already uploaded)
        name: filename,
        size: 0, // Unknown size for restored files
        progress: 100,
        status: 'success',
        blobPath: blobPath,
        error: null,
      };
    });

    setFiles(reconstructedFiles);
  }, [value]); // Only depend on value, not files (to avoid infinite loop)

  // Handle file selection
  const handleFiles = async (selectedFiles) => {
    if (disabled) return;

    const fileArray = Array.from(selectedFiles);

    // Check max files limit
    if (files.length + fileArray.length > maxFiles) {
      toast.error(`You can only upload up to ${maxFiles} files`);
      return;
    }

    // Validate and prepare files
    const newFiles = [];
    for (const file of fileArray) {
      // Validate file
      if (storageConfig) {
        const validation = storageService.validateFile(file, storageConfig);
        if (!validation.isValid) {
          toast.error(validation.error);
          continue;
        }
      }

      newFiles.push({
        id: Date.now() + Math.random(),
        file,
        name: file.name,
        size: file.size,
        progress: 0,
        status: 'pending', // pending, uploading, success, error
        blobPath: null,
        error: null,
      });
    }

    if (newFiles.length === 0) return;

    setFiles(prev => [...prev, ...newFiles]);

    // Start uploads
    for (const fileItem of newFiles) {
      uploadFile(fileItem);
    }
  };

  // Upload single file
  const uploadFile = async (fileItem) => {
    try {
      // Update status
      updateFileStatus(fileItem.id, { status: 'uploading' });

      // Upload directly via per-file presigned URL
      const { blobPath } = await storageService.uploadFileToBlob(
        fileItem.file,
        (loaded, total, percentage) => {
          updateFileStatus(fileItem.id, { progress: percentage });
        }
      );

      // Mark as success and update parent with new blob path
      setFiles(prev => {
        const updatedFiles = prev.map(f =>
          f.id === fileItem.id ? { ...f, status: 'success', progress: 100, blobPath } : f
        );

        // Extract all successful blob paths
        const blobPaths = updatedFiles
          .filter(f => f.status === 'success' && f.blobPath)
          .map(f => f.blobPath);

        // Update parent component
        onChange(blobPaths);

        return updatedFiles;
      });

      toast.success(`${fileItem.name} uploaded successfully`);
    } catch (error) {
      console.error('Upload failed:', error);
      updateFileStatus(fileItem.id, {
        status: 'error',
        error: error.message
      });
      toast.error(`Failed to upload ${fileItem.name}: ${error.message}`);
    }
  };

  // Update file status
  const updateFileStatus = (fileId, updates) => {
    setFiles(prev => prev.map(f =>
      f.id === fileId ? { ...f, ...updates } : f
    ));
  };

  // Remove file
  const handleRemove = (fileId) => {
    setFiles(prev => {
      const fileToRemove = prev.find(f => f.id === fileId);
      const updatedFiles = prev.filter(f => f.id !== fileId);

      // Update parent with remaining successful uploads
      const blobPaths = updatedFiles
        .filter(f => f.status === 'success' && f.blobPath)
        .map(f => f.blobPath);
      onChange(blobPaths);

      if (fileToRemove?.status === 'success') {
        toast.info(`${fileToRemove.name} removed`);
      }

      return updatedFiles;
    });
  };

  // Drag and drop handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragActive(false);

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      handleFiles(droppedFiles);
    }
  };

  // Click to browse
  const handleClick = () => {
    if (!disabled) {
      fileInputRef.current?.click();
    }
  };

  const handleInputChange = (e) => {
    const selectedFiles = e.target.files;
    if (selectedFiles.length > 0) {
      handleFiles(selectedFiles);
    }
    // Reset input value to allow re-uploading same file
    e.target.value = '';
  };

  return (
    <Container theme={theme}>
      <DropZone
        theme={theme}
        isDragActive={isDragActive}
        onDragEnter={handleDragEnter}
        onDragLeave={handleDragLeave}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        <DropZoneIcon><FolderOpen size={48} /></DropZoneIcon>
        <DropZoneText theme={theme}>
          {isDragActive ? 'Drop files here' : 'Click or drag files here to upload'}
        </DropZoneText>
        <DropZoneSubtext theme={theme}>
          {storageConfig
            ? `Max ${storageConfig.maxFileSizeMB}MB per file • Up to ${maxFiles} files`
            : `Up to ${maxFiles} files`}
        </DropZoneSubtext>
      </DropZone>

      <HiddenInput
        ref={fileInputRef}
        type="file"
        multiple
        accept={accept}
        onChange={handleInputChange}
        disabled={disabled}
      />

      {files.length > 0 && (
        <FileList theme={theme}>
          {files.map(file => (
            <FileItem key={file.id} theme={theme}>
              <FileIcon>
                {file.status === 'success' ? <CheckCircle2 size={24} /> :
                 file.status === 'error' ? <XCircle size={24} /> :
                 file.status === 'uploading' ? <Clock size={24} /> : <FileText size={24} />}
              </FileIcon>
              <FileInfo>
                <FileName theme={theme}>{file.name}</FileName>
                <FileSize theme={theme}>{formatFileSize(file.size)}</FileSize>
                {file.status === 'uploading' && (
                  <ProgressBar theme={theme}>
                    <ProgressFill theme={theme} progress={file.progress} />
                  </ProgressBar>
                )}
                {file.status === 'success' && (
                  <FileStatus theme={theme} status="success">
                    Uploaded successfully
                  </FileStatus>
                )}
                {file.status === 'error' && (
                  <FileStatus theme={theme} status="error">
                    {file.error || 'Upload failed'}
                  </FileStatus>
                )}
              </FileInfo>
              <RemoveButton
                theme={theme}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove(file.id);
                }}
              >
                <XIcon size={14} />
              </RemoveButton>
            </FileItem>
          ))}
        </FileList>
      )}
    </Container>
  );
};

export default FileUpload;
